import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { useScreen } from '../../utils/responsive-helper'
import Tabs from '../../components/common/Tabs'
import Card from '../../components/common/Card'
import Image from '../../components/common/Image'
import Text from '../../components/common/Text'
import Tag from '../../components/common/Tag'
import coin from '../../assets/images/icons-coins.svg'
import { PRIMARY, SECONDARY } from '../../constant/colors'
import Button from '../../components/common/Button'
import { useGetNews, useGetNewsType } from '../../services/digital-news/digital-news-query'
import { useTranslation } from 'react-i18next'
import * as paths from '../../constant/path'
import { NewsType, NewsWithPaging } from '../../services/digital-news/digital-news-types'
import { useGetUser } from '../../services/user/user-query'
import Loading from '../../components/common/Loading'
import { Link } from 'react-router-dom'
import { useRouter } from '../../utils/helper'
import { useHistory } from 'react-router'

const CategoryContainer = styled.div`
  /* border: 1px solid #000000; */
`

const TabContainer = styled.div<{ isMobile: boolean }>`
  /* border: 1px solid #000000; */
  margin-left: 0px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 267px);
  grid-gap: 24px;
  justify-content: ${(isMobile) => (isMobile ? 'center' : 'flex-start')};
  margin-bottom: 15px;
`

const NewsCardDiv = styled(Card)`
  width: 267px;
  height: 330px;
  overflow: hidden;
`

const NewsCardStatus = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  margin-top: 18px;
  margin-bottom: 17px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const NewsCardText = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 24px;
`

const MobileTag = styled(Tag)`
  width: 75px;
  height: 17px;
  padding: 2px 8px 0;
  border-radius: 3px;
`

const CustomDiv = styled.div`
  /* border: 1px solid #000000; */
  display: flex;
  flex-direction: row;
`

const CoinImage = styled(Image)`
  object-fit: contain;
  width: 16px;
  height: 16px;
  margin-left: 8px;
`

const GreenCircle = styled.div`
  background-color: ${SECONDARY};
  height: 18px;
  width: 18px;
  border-radius: 9px;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  margin-bottom: 41px;
  width: 100%;
`

type CategoryPageProps = {
  name: string
}

type NewsCardProps = {
  news: NewsType
}

const NewsCard = (props: NewsCardProps) => {
  const { news } = props
  const { digitalNews, isRead } = news

  const newsPath = useMemo(() => {
    return paths.newsDetails({ routeParam: { newsId: news.digitalNews.id } })
  }, [news])

  return (
    <Link to={newsPath}>
      <NewsCardDiv onClick={() => { }}>
        <Image
          src={`${process.env.REACT_APP_API_HOST}${digitalNews.thumbnailPath}`}
          style={{ objectFit: 'cover', width: 267, height: 189 }}
        />
        <NewsCardStatus>
          <CustomDiv>
            <MobileTag fontSize={9.6} label={news.digitalNews.type} type={news.digitalNews.type} />
            <CoinImage src={coin} />
          </CustomDiv>
          {!isRead ? <GreenCircle /> : null}
        </NewsCardStatus>
        <NewsCardText>
          <Text ellipsis maxLine={2} size={16}>
            {digitalNews.title}
          </Text>
        </NewsCardText>
      </NewsCardDiv>
    </Link>
  )
}

const CategoryPage = (props: CategoryPageProps) => {
  const { t } = useTranslation()
  const { name } = props
  const { data: user } = useGetUser()
  const { userId } = user || {}
  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useGetNews({
    size: 12,
    userId: userId,
    category: name,
    subCategory: 'Digital News',
  })
  const { isMobile } = useScreen()

  const onFetchMoreNews = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  return (
    <Loading style={{ height: 100 }} loading={isLoading}>
      <TabContainer isMobile={isMobile}>
        {data &&
          data.pages.map(({ data: page }: NewsWithPaging) =>
            page.map((news: NewsType) => <NewsCard key={news.digitalNews.id} news={news} />),
          )}
      </TabContainer>
      <LoadMoreContainer>
        <Loading loading={!isLoading && isFetching}>
          <Button onClick={onFetchMoreNews} disabled={!hasNextPage}>
            {t('news.more')}
          </Button>
        </Loading>
      </LoadMoreContainer>
    </Loading>
  )
}

const Category = () => {
  const { query } = useRouter<{ type?: string }>()
  const { type = 'All' } = query || {}
  const { isDesktop } = useScreen()
  const history = useHistory()

  const { data: categoryTabs } = useGetNewsType()

  const dataSource = useMemo(() => {
    return categoryTabs
      ? categoryTabs.map((tab, idx) => ({
        title: tab.name,
        key: idx,
        imageIcon: tab.logoPath,
        icon: tab.icon,
        page: <CategoryPage name={tab.name} />,
      }))
      : []
  }, [categoryTabs])

  const convertNewsTypeToKey = useCallback(
    (type: string) => {
      return dataSource.find(({ title }) => title === type)?.key
    },
    [dataSource],
  )

  const convertNewsKeyToType = useCallback(
    (activeKey: string | number) => {
      return dataSource.find(({ key }) => key === activeKey)?.title
    },
    [dataSource],
  )

  const onTabsChange = useCallback(
    (key: string) => {
      const type = convertNewsKeyToType(key) || ''
      history.push(
        paths.news({
          queryParam: {
            type,
          },
        }),
      )
    },
    [convertNewsKeyToType, history],
  )

  const newsKey = useMemo(() => {
    return type && convertNewsTypeToKey(type)
  }, [type, convertNewsTypeToKey])

  return (
    <CategoryContainer>
      {categoryTabs && dataSource.length && (
        <Tabs
          activeKey={newsKey}
          dataSource={dataSource}
          mode={isDesktop ? undefined : 'select'}
          onChange={onTabsChange}
        />
      )}
    </CategoryContainer>
  )
}

export default Category
